import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import AceEditor from 'react-ace'
import 'ace-builds/src-noconflict/mode-json'
import 'ace-builds/src-noconflict/theme-xcode'
import SydButton from '../../../../../commonDesign/Button'
import FlexRow from '../../../../../molecules/FlexRow'

function JsonEditor ({ value, onChange, height = '150px' }) {
  const [text, setText] = useState(() => JSON.stringify(value ?? {}, null, 2))
  const [error, setError] = useState(null)
  const [hasBlurred, setHasBlurred] = useState(false)
  const latestTextRef = useRef(text)

  useEffect(() => {
    const incoming = JSON.stringify(value ?? {}, null, 2)
    if (incoming !== latestTextRef.current && !error) {
      setText(incoming)
      latestTextRef.current = incoming
    }
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = useCallback((newText) => {
    setText(newText)
    latestTextRef.current = newText
    try {
      JSON.parse(newText)
      setError(null)
    } catch {
      if (hasBlurred) {
        setError('Invalid JSON')
      }
    }
  }, [hasBlurred])

  const handleBlur = useCallback(() => {
    setHasBlurred(true)
    try {
      const parsed = JSON.parse(text)
      setError(null)
      onChange(parsed)
    } catch {
      setError('Invalid JSON')
    }
  }, [text, onChange])

  const handleFormat = useCallback(() => {
    try {
      const parsed = JSON.parse(text)
      const formatted = JSON.stringify(parsed, null, 2)
      setText(formatted)
      latestTextRef.current = formatted
      setError(null)
    } catch {
      setError('Cannot format -- invalid JSON')
    }
  }, [text])

  return (
    <div>
      <AceEditor
        mode='json'
        theme='xcode'
        value={text}
        onChange={handleChange}
        onBlur={handleBlur}
        width='100%'
        height={height}
        fontSize={12}
        showPrintMargin={false}
        showGutter
        highlightActiveLine
        setOptions={{
          showLineNumbers: true,
          tabSize: 2,
          useWorker: false
        }}
        editorProps={{ $blockScrolling: true }}
      />
      <FlexRow style={{ justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
        <div>
          {error && (
            <span style={{ color: 'var(--color-danger-500)', fontSize: '0.75rem' }}>
              {error}
            </span>
          )}
        </div>
        <SydButton variant='outline' size='sm' onClick={handleFormat}>
          Format JSON
        </SydButton>
      </FlexRow>
    </div>
  )
}

JsonEditor.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  height: PropTypes.string
}

export default JsonEditor
